import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid-pro'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { EmptyDocumentReviewQueue } from 'src/components/document-review/queue/EmptyDocumentReviewQueue'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  CONTRACTOR_QUEUE_GRID_COLUMNS_STORAGE_KEY,
  documentReviewPageSizeOptions,
} from 'src/constants/document-review/constants'
import { contractorQueueColumns } from 'src/constants/document-review/contractorQueueGrid'
import { ContractorQueueFields } from 'src/constants/document-review/enums'
import { ADMIN_ROUTES } from 'src/constants/routes'
import type { DocumentReviewQueueRow } from 'src/interfaces/documentReview'
import { getDocumentReviewSteps, getDocumentReviewStepsLoading } from 'src/selectors/documentReview'
import { filterContractorQueue } from 'src/utils/documentReviewList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
}

const getRowId = (row: DocumentReviewQueueRow) => {
  return row.id
}

// Scoped to the rows, not the grid: a bare `cursor-pointer` would also cover the empty space
// below the last row and the pagination footer, neither of which opens a review.
const rowPointerStyles = { '& .MuiDataGrid-row': tw`cursor-pointer` }

const UNHIDEABLE_COLUMNS: Array<string> = [ContractorQueueFields.driverName]

const ContractorQueueTableComponent = ({ searchQuery }: Props) => {
  const navigate = useNavigate()

  const steps = useSelector(getDocumentReviewSteps)
  const isLoading = useSelector(getDocumentReviewStepsLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(documentReviewPageSizeOptions)
  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    contractorQueueColumns,
    CONTRACTOR_QUEUE_GRID_COLUMNS_STORAGE_KEY,
  )

  // The endpoint returns the whole queue and takes no paging or search params, so search is
  // applied here rather than sent to the backend.
  const visibleSteps = useMemo(() => {
    return filterContractorQueue(steps, searchQuery)
  }, [steps, searchQuery])

  const isSearchUsed = searchQuery.trim() !== ''
  const EmptyElement = useMemo(() => {
    return () => {
      return <EmptyDocumentReviewQueue isSearchUsed={isSearchUsed} />
    }
  }, [isSearchUsed])

  const columnVisibilityModel = useMemo(() => {
    return contractorQueueColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [includedColumns])

  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      contractorQueueColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const handleRowClick = useCallback(
    (params: GridRowParams<DocumentReviewQueueRow>) => {
      navigate(
        ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_CONTRACTOR_URL.replace(':stepId', String(params.id)),
      )
    },
    [navigate],
  )

  return (
    <GridTable<DocumentReviewQueueRow>
      rows={visibleSteps}
      columns={contractorQueueColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={documentReviewPageSizeOptions}
      initialState={{
        // Oldest first: the queue is worked front to back, so the longest wait is the next job.
        sorting: { sortModel: [{ field: ContractorQueueFields.finishedAt, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="document-review-contractor-table"
      rowDataTestIdPrefix="document-review-contractor"
      onRowClick={handleRowClick}
      className={rowPointerStyles}
      fillHeight
    />
  )
}

export const ContractorQueueTable = memo(ContractorQueueTableComponent)
